import React from "react";
import { UIBox, UICircularProgress, UIDialog, UIDialogActions, UIDialogContent, UIDialogTitle } from "..";
import UIButton from "./UIButton";
import UITypography from "./UITypography";
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material/styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export interface UIModalDialogAction {
  label: string;
  onClick: () => void;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "error" | "success" | "warning";
  icon?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
}

export interface UIModalDialogProps {
  open: boolean;
  onClose: () => void;
  type?: "info" | "warn" | "error" | "success";
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: UIModalDialogAction[];
  maxWidth?: "xs" | "sm" | "md" | "lg";
  fullWidth?: boolean;
}

const getTypeIcon = (type: UIModalDialogProps["type"]) => {
  switch (type) {
    case "warn":
    case "error":
      return <WarningIcon />;
    case "success":
      return <CheckCircleIcon />;
    case "info":
    default:
      return <PersonAddIcon />;
  }
};

const getTypeColor = (type: UIModalDialogProps["type"], theme: Theme) => {
  switch (type) {
    case "warn":
    case "error":
      return theme.palette.error.main;
    case "success":
      return theme.palette.success.main;
    case "info":
    default:
      return theme.palette.primary.main;
  }
};

const UIModalDialog = ({
  open,
  onClose,
  type = "info",
  icon,
  title,
  subtitle,
  children,
  actions = [],
  maxWidth = "sm",
  fullWidth = true,
}: UIModalDialogProps) => {
  const classes = useStyles();

  const displayIcon = icon || getTypeIcon(type);

  return (
    <UIDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <UIDialogTitle className={classes.dialogTitle}>
        <UIBox className={classes.titleContent}>
          <UIBox 
            className={classes.titleIcon}
            sx={{
              color: (theme: Theme) => getTypeColor(type, theme)
            }}
          >
            {displayIcon}
          </UIBox>
          <UIBox>
            <UITypography variant="h5" component="h2" className={classes.title}>
              {title}
            </UITypography>
            {subtitle && (
              <UITypography variant="body2" className={classes.subtitle}>
                {subtitle}
              </UITypography>
            )}
          </UIBox>
        </UIBox>
      </UIDialogTitle>

      <UIDialogContent className={classes.dialogContent}>
        {children}
      </UIDialogContent>

      {actions.length > 0 && (
        <UIDialogActions className={classes.dialogActions}>
          <UIBox className={classes.actionButtons}>
            {actions.map((action, index) => (
              <UIButton
                key={index}
                onClick={action.onClick}
                variant={action.variant || "contained"}
                color={action.color || "primary"}
                disabled={action.disabled || action.loading}
                startIcon={
                  action.loading ? (
                    <UICircularProgress size={18} color="inherit" />
                  ) : (
                    action.icon
                  )
                }
                className={
                  action.variant === "text" 
                    ? classes.secondaryButton 
                    : classes.primaryButton
                }
              >
                {action.label}
              </UIButton>
            ))}
          </UIBox>
        </UIDialogActions>
      )}
    </UIDialog>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    padding: theme.spacing(3, 3, 2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
  },
  titleIcon: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontWeight: 600,
    marginBottom: 4,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    padding: theme.spacing(2, 3, 3, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    width: "100%",
  },
  secondaryButton: {
    minWidth: 100,
  },
  primaryButton: {
    minWidth: 140,
  },
}));

export default UIModalDialog;